import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, cardImageUrl } from "@/lib/api";
import { useGame, GAMES } from "@/lib/game";
import { Input } from "@/components/ui/input";
import { Search, PackageX } from "lucide-react";
import { isSealed, sealedLabel } from "@/lib/productType";

/**
 * Live typeahead for the catalog. Debounces user input, calls
 * /catalog/suggest, and shows a floating dropdown with mini-cards.
 *
 * On Enter (or clicking the search button) the parent form submits and the
 * user lands on /catalog?q=... where the same flexible backend search runs.
 *
 * Props:
 *   value / onChange — controlled input
 *   onSubmit         — form submit handler (Enter key path)
 *   size             — "hero" (Home) | "compact" (Catalog toolbar)
 *   testidPrefix     — data-testid namespace (default "search")
 */
export function SearchTypeahead({ value, onChange, onSubmit, size = "compact", testidPrefix = "search", placeholder }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const wrapRef = useRef(null);
  const panelRef = useRef(null);
  const [rect, setRect] = useState(null);
  const nav = useNavigate();
  const { activeGame } = useGame();

  // Debounce fetches — 180ms feels instant but avoids hammering the API on
  // every keystroke.
  useEffect(() => {
    const q = (value || "").trim();
    if (q.length < 2) {
      setItems([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        // Fix (feb 2026): pasar `game` al typeahead — antes las sugerencias
        // podían mostrar cartas Riftbound estando en la pestaña Pokémon.
        const r = await api.get("/catalog/suggest", { params: { q, limit: 8, game: activeGame } });
        setItems(r.data || []);
        setOpen(true);
        setActiveIdx(-1);
      } catch {
        setItems([]);
      } finally { setLoading(false); }
    }, 180);
    return () => clearTimeout(t);
  }, [value, activeGame]);

  // Close on outside click.
  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const showPanel = open && (items.length > 0 || loading);

  // Panel y fondo van en portal (position: fixed) para quedar sobre la grilla.
  useLayoutEffect(() => {
    if (!showPanel) { return undefined; }
    const measure = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (r) setRect({ top: r.top, bottom: r.bottom, left: r.left, right: r.right, vw: window.innerWidth, vh: window.innerHeight });
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => { window.removeEventListener("resize", measure); window.removeEventListener("scroll", measure, true); };
  }, [showPanel]);

  const pick = (it) => {
    setOpen(false);
    const q = (value || "").trim();
    // Land the user on catalog with the query preserved so the results page
    // shows the same set (plus filters). Direct-to-card would skip alt langs.
    nav(`/catalog?q=${encodeURIComponent(it.name || q)}`);
  };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIdx(i => Math.min(items.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx(i => Math.max(-1, i - 1)); }
    else if (e.key === "Enter" && activeIdx >= 0) { e.preventDefault(); pick(items[activeIdx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  const inputCls = size === "hero"
    ? "pl-11 h-14 bg-[#171717] border-[#262626] text-white placeholder:text-neutral-500 focus-visible:ring-[#EF4444]"
    : "pl-10 pr-14 lg:pr-28 h-[50px] rounded-[14px] bg-[#101010] border-white/10 text-white placeholder:text-white/35 focus-visible:ring-0 focus-visible:border-[#FF4438] focus-visible:border-[1.5px] focus-visible:shadow-[0_0_0_4px_rgba(255,68,56,.14)]";

  return (
    <div ref={wrapRef} className="relative flex-1">
      <Search className={`absolute left-4 ${size === "hero" ? "top-1/2 -translate-y-1/2" : "top-1/2 -translate-y-1/2"} text-neutral-500`} size={size === "hero" ? 18 : 16} />
      <Input
        data-testid={`${testidPrefix}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => { if (items.length > 0) setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder={placeholder || "¿Qué carta buscas? Ej: char, pika, mewtwo…"}
        className={inputCls}
        autoComplete="off"
      />
      {showPanel && rect && createPortal(<>
        <div
          aria-hidden
          data-testid={`${testidPrefix}-backdrop`}
          onMouseDown={() => setOpen(false)}
          className="fixed inset-0 z-[70] bg-black/60"
          style={{ clipPath: (() => {
            const l = rect.left - 4, t = rect.top - 4, r = rect.right + 4, b = rect.bottom + 4, R = 18;
            return `path(evenodd, "M0 0H${rect.vw}V${rect.vh}H0Z M${l + R} ${t}H${r - R}A${R} ${R} 0 0 1 ${r} ${t + R}V${b - R}A${R} ${R} 0 0 1 ${r - R} ${b}H${l + R}A${R} ${R} 0 0 1 ${l} ${b - R}V${t + R}A${R} ${R} 0 0 1 ${l + R} ${t}Z")`;
          })() }}
        />
        <div
          ref={panelRef}
          data-testid={`${testidPrefix}-dropdown`}
          className="sx fixed z-[71] bg-[#0f0f0f] border border-white/10 rounded-[22px] shadow-[0_24px_60px_-20px_rgba(0,0,0,.8)] overflow-y-auto p-1.5"
          style={rect.vw < 1024
            ? { top: rect.bottom + 8, left: 20, right: 20, maxHeight: Math.min(440, rect.vh - rect.bottom - 24) }
            : { top: rect.bottom + 8, left: rect.left, width: rect.right - rect.left, maxHeight: Math.min(440, rect.vh - rect.bottom - 24) }}
        >
          <div className="sx-label px-3 pt-2 pb-1.5" data-testid={`${testidPrefix}-dropdown-label`}>Coincidencias · {GAMES[activeGame]?.label}</div>
          {loading && items.length === 0 && (
            <div className="p-3 text-xs text-neutral-500">Buscando…</div>
          )}
          {items.map((it, i) => {
            const sealed = isSealed(it);
            const bundle = !!it.is_bundle;
            return (
            <button
              key={`${it.card_id}-${it.language}`}
              type="button"
              data-testid={`${testidPrefix}-suggestion-${it.card_id}`}
              onMouseDown={(e) => { e.preventDefault(); pick(it); }}
              onMouseEnter={() => setActiveIdx(i)}
              className={`w-full flex items-center gap-3 px-2.5 py-2 text-left rounded-[14px] transition-colors ${i === activeIdx ? "bg-white/[.06]" : "hover:bg-white/[.06]"}`}
            >
              <div className="w-10 h-[55px] shrink-0 bg-[#080808] rounded-md overflow-hidden flex items-center justify-center border border-white/10">
                {it.image_url
                  ? <img src={cardImageUrl(it.image_url)} alt="" className="w-full h-full object-contain" onError={(e) => e.currentTarget.style.display = "none"} />
                  : <div className="text-[9px] text-neutral-600">—</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <div className="text-sm font-bold text-white line-clamp-1 flex-1">{it.name}</div>
                  {sealed && (
                    <span className="sx-mono text-[8px] uppercase px-1.5 py-[3px] rounded-md bg-white text-[#080808]" data-testid={`${testidPrefix}-sealed-${it.card_id}`}>
                      {sealedLabel(it)}
                    </span>
                  )}
                  {!sealed && bundle && (
                    <span className="sx-mono text-[8px] uppercase px-1.5 py-[3px] rounded-md bg-white text-[#080808]" data-testid={`${testidPrefix}-bundle-${it.card_id}`}>
                      Colección
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-white/50 line-clamp-1">
                  {it.set_name}{it.number ? ` · #${it.number}` : ""}
                  {it.name_en && it.name_en !== it.name ? ` · ${it.name_en}` : ""}
                </div>
              </div>
              <div className="text-right shrink-0 flex flex-col items-end gap-1">
                {it.in_stock && it.min_price != null
                  ? <div className="text-[15px] font-extrabold tracking-[-.02em] text-[#FF4438]">${Number(it.min_price).toFixed(2)}</div>
                  : <div className="sx-mono text-[9px] uppercase text-white/50 inline-flex items-center gap-1"><PackageX size={10} /> sin stock</div>}
                <span className="sx-mono text-[8px] uppercase px-1.5 py-[2px] rounded-md bg-[#FF4438] text-[#080808]">{it.language}</span>
              </div>
            </button>
          );})}
          {!loading && items.length > 0 && (
            <button
              type="button"
              data-testid={`${testidPrefix}-see-all`}
              onMouseDown={(e) => { e.preventDefault(); setOpen(false); onSubmit?.(new Event("submit")); }}
              className="w-full mt-1 px-3 py-3 text-[13px] font-semibold text-left text-[#ff8a80] hover:bg-white/[.06] rounded-[14px] border-t border-white/10 transition-colors"
            >
              Ver todos los resultados de “{value}” →
            </button>
          )}
        </div>
      </>, document.body)}
    </div>
  );
}

export default SearchTypeahead;
